import { useCallback, useState } from "react";
import { default as axios } from "axios";
import useApi from "./useApi";
import { API } from "../utils/constants";

/** Allowed image content types (BLU-007 §6 D3). */
export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

/** Max image size in bytes (~5 MB, BLU-007 §6 D3). */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/**
 * Validates a picked file against the BLU-007 type/size constraints.
 * @param {File} file
 * @returns {string|null} an error message, or null if valid.
 */
export const validateImageFile = (file) => {
  if (!file) return "No file selected.";
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return "Image must be a JPEG, PNG, or WebP file.";
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return "Image must be 5 MB or smaller.";
  }
  return null;
};

/**
 * useMenuImageUpload — the BLU-007 SAS client-direct upload flow for a menu item
 * image (SPR-009, CON-002 §6).
 *
 * 1. POST /api/admin/menu/{id}/image/upload-url { fileName, contentType, sizeBytes }
 *      → { uploadUrl, blobUrl }   (short-lived write-only SAS + public read URL)
 * 2. PUT the raw file bytes DIRECTLY to `uploadUrl` (Azure Blob) — a plain axios
 *      PUT with `x-ms-blob-type: BlockBlob` + the file's content-type. This MUST
 *      bypass the `useApi` axios instance (no auth header — it's a SAS URL).
 * 3. PUT /api/admin/menu/{id}/image { imageUrl: blobUrl } to persist.
 *
 * `removeImage` calls DELETE /api/admin/menu/{id}/image.
 *
 * Never throws — resolves to `{ imageUrl }` on success or `{ error }` on failure.
 *
 * @returns {{
 *   uploadImage: (id: number, file: File) => Promise<{imageUrl?: string, error?: string}>,
 *   removeImage: (id: number) => Promise<{imageUrl?: string|null, error?: string}>,
 *   uploading: boolean,
 * }}
 */
export default function useMenuImageUpload() {
  const { post, put, del } = useApi();
  const [uploading, setUploading] = useState(false);

  const uploadImage = useCallback(
    async (id, file) => {
      const validationError = validateImageFile(file);
      if (validationError) return { error: validationError };

      setUploading(true);
      try {
        // 1. Request a write-only SAS for a single blob.
        const { uploadUrl, blobUrl } = await post(API.ADMIN.MENU.IMAGE_UPLOAD_URL(id), {
          fileName: file.name,
          contentType: file.type,
          sizeBytes: file.size,
        });

        if (!uploadUrl || !blobUrl) {
          return { error: "The server did not return an upload URL." };
        }

        // 2. PUT the raw bytes directly to Azure Blob via the SAS URL.
        //    Plain axios — NOT the useApi instance (a Bearer header would break
        //    the SAS signature, and the SAS URL is not under /api).
        await axios.put(uploadUrl, file, {
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": file.type,
          },
        });

        // 3. Persist the public blob URL on the menu item.
        await put(API.ADMIN.MENU.IMAGE_SET(id), { imageUrl: blobUrl });

        return { imageUrl: blobUrl };
      } catch (err) {
        const detail = err?.data?.detail;
        return { error: detail || "Image upload failed. Please try again." };
      } finally {
        setUploading(false);
      }
    },
    [post, put]
  );

  const removeImage = useCallback(
    async (id) => {
      setUploading(true);
      try {
        await del(API.ADMIN.MENU.IMAGE_DELETE(id));
        return { imageUrl: null };
      } catch (err) {
        const detail = err?.data?.detail;
        return { error: detail || "Failed to remove the image. Please try again." };
      } finally {
        setUploading(false);
      }
    },
    [del]
  );

  return { uploadImage, removeImage, uploading };
}
